/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';

import PlayerCardV3 from '../../../../../components/cards/PlayerCardV3';
import Rating from '../../../../../components/cards/PlayerCard/SharedComponents/Rating';
import { bucketWildcardTags, sportTypeIds, utrTypeIds } from '../../../../../utils/eventConstants';
import { getPlayerPaymentStatus, getPlayerRegStatusV3, getPlayerRegDateV3 } from '../../../../../utils';
import { gameTypes } from '../../../../../utils/constants';
import AvatarV2 from '../../../../../components/cards/PlayerCard/ListViewComponents/AvatarV2';
import PlayerDetails from '../../../../../components/cards/PlayerCard/ListViewComponents/PlayerDetails';
import { getPlayerCardPlayerDetails } from '../../../../../utils/formatting';
import { authShape, menuOptionShape, playerShape, timeZoneShape } from '../../../../../propTypes';

const AcceptancePlayerCard = props => {
    const {
        auth,
        currentDivisionId,
        gridLayout,
        hideUtrLabel,
        isCurrentMemberAdmin,
        player,
        showDecimals,
        showHighSchoolName,
        showCollegeName,
        showPickleBallRatings = true,
        showRatingNotLoggedIn,
        showAgeColumn,
        showRegDate,
        showRegStatus,
        showTennisRatings = true,
        sportTypeId,
        utrTypeId,
        isPttEvent,
        timeZone,
    } = props;

    const user = auth?.user;

    const { firstName, lastName, nationality, playerProfileImages, ratingStatusSingles, ratingStatusDoubles } = player;

    const descriptionItems = getPlayerCardPlayerDetails(player, isCurrentMemberAdmin, showHighSchoolName, !showAgeColumn);

    const singlePkbRatings = player.singlespkbrating || player.historicpkbsinglesrating || player.singlesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const doublesPkbRatings = player.doublespkbrating || player.historicpkbdoublesrating || player.doublesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const hidePlayer = sportTypeId === sportTypeIds.PICKLEBALL && !singlePkbRatings && !doublesPkbRatings;

    const playerDivision = player?.divisions?.find(div => div.id === currentDivisionId);

    const regStatus = getPlayerRegStatusV3(playerDivision);
    const paymentStatus = getPlayerPaymentStatus(playerDivision?.paymentStatusTypeId);

    const isPlayerLoggedInUser = player?.memberId === user?.id;

    if (hidePlayer) {
        return null;
    }

    return (
        <PlayerCardV3
            highlightCard={isPlayerLoggedInUser}
            {...props}
            player={{
                ...player,
                id: player.playerId,
            }}
        >
            <div className={`col-12`}>
                <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                    <div className={`d-flex aic oh pr8`}>
                        <AvatarV2
                            playerImages={playerProfileImages}
                            nationality={nationality}
                            player={player}
                        />
                        <PlayerDetails
                            firstName={firstName}
                            lastName={lastName}
                            description={descriptionItems}
                            playerId={player.playerId}
                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                            showOrganizerViewForPTTEvent={isPttEvent && isCurrentMemberAdmin}
                            showCollegeGradYear={false}
                            college={showCollegeName ? player.college : null}
                            showTag={isCurrentMemberAdmin}
                            tag={bucketWildcardTags[player.acceptanceWildcardStateId]}
                            showSelfProfileIcon={isPlayerLoggedInUser}
                        />
                    </div>
                    {showAgeColumn && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ellipsis`}>
                                {player.age || '--'}
                            </div>
                        </div>
                    )}
                    {showTennisRatings && (
                        <>
                            <div className={`d-flex aic pr8`}>
                                {(user || showRatingNotLoggedIn) && (
                                    <Rating
                                        utrType={utrTypeId}
                                        ratingType={gameTypes.SINGLES}
                                        ratingStatus={ratingStatusSingles}
                                        player={player}
                                        showDecimals={showDecimals}
                                        hideUtrLabel={hideUtrLabel}
                                        sportTypeId={sportTypeId}
                                        showTypeDots={false}
                                    />
                                )}
                            </div>
                            <div className={`d-flex aic pr8`}>
                                {(user || showRatingNotLoggedIn) && (
                                    <Rating
                                        utrType={utrTypeId}
                                        ratingType={gameTypes.DOUBLES}
                                        ratingStatus={ratingStatusDoubles}
                                        player={player}
                                        showDecimals={showDecimals}
                                        hideUtrLabel={hideUtrLabel}
                                        sportTypeId={sportTypeId}
                                        showTypeDots={false}
                                    />
                                )}
                            </div>
                        </>
                    )}
                    {showPickleBallRatings && (
                        <>
                            <div className={`d-flex aic pr8`}>
                                {(user || showRatingNotLoggedIn) && (
                                    <Rating
                                        utrType={utrTypeId}
                                        ratingType={gameTypes.SINGLES}
                                        player={player}
                                        showDecimals={showDecimals}
                                        hideUtrLabel={hideUtrLabel}
                                        sportTypeId={sportTypeId}
                                        isPickleballRating
                                    />
                                )}
                            </div>
                            <div className={`d-flex aic pr8`}>
                                <Rating
                                    utrType={utrTypeId}
                                    ratingType={gameTypes.DOUBLES}
                                    player={player}
                                    showDecimals={showDecimals}
                                    hideUtrLabel={hideUtrLabel}
                                    sportTypeId={sportTypeId}
                                    isPickleballRating
                                />
                            </div>
                        </>
                    )}
                    {isPttEvent && (
                        <div className={`d-flex aic pr8`}>
                            <div className="values">
                                <div
                                    className={'value'}
                                    title={utrTypeId === utrTypeIds.SANCTIONED ? ratingStatusDoubles : ''}
                                >
                                    {player.rank || '--'}
                                </div>
                            </div>
                        </div>
                    )}
                    {showRegDate && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ellipsis`}>
                                {getPlayerRegDateV3(playerDivision, timeZone?.value) || '--'}
                            </div>
                        </div>
                    )}
                    {showRegStatus && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ${regStatus.color} ellipsis`} title={regStatus.label}>
                                {regStatus.label}
                            </div>
                        </div>
                    )}
                    {isCurrentMemberAdmin && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ${paymentStatus.color} ellipsis`} title={paymentStatus.label}>
                                {paymentStatus.label || '--'}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </PlayerCardV3>
    );
};

AcceptancePlayerCard.propTypes = {
    addOptionsSpace: PropTypes.bool,
    auth: authShape.isRequired,
    currentDivisionId: PropTypes.string,
    gridLayout: PropTypes.arrayOf(PropTypes.string).isRequired,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isSelected: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(menuOptionShape),
    onMenuSelected: PropTypes.func,
    onSelectClick: PropTypes.func,
    player: playerShape.isRequired,
    sendMessage: PropTypes.func,
    showCheckbox: PropTypes.bool,
    showDecimals: PropTypes.bool,
    showHighSchoolName: PropTypes.bool,
    showCollegeName: PropTypes.bool,
    showPickleBallRatings: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showRegStatus: PropTypes.bool,
    showTennisRatings: PropTypes.bool,
    sportTypeId: PropTypes.number,
    utrTypeId: PropTypes.number,
    isPttEvent: PropTypes.bool,
    timeZone: timeZoneShape.isRequired,
    showAgeColumn: PropTypes.bool,
    showRegDate: PropTypes.bool,
    showPosition: PropTypes.bool,
};

AcceptancePlayerCard.defaultProps = {
    addOptionsSpace: true,
    currentDivisionId: null,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isSelected: false,
    menuOptions: null,
    onMenuSelected: () => { },
    onSelectClick: () => { },
    sendMessage: () => { },
    showDecimals: false,
    showCheckbox: false,
    showHighSchoolName: false,
    showCollegeName: false,
    showPickleBallRatings: false,
    showRatingNotLoggedIn: false,
    showRegStatus: false,
    showTennisRatings: false,
    sportTypeId: sportTypeIds.TENNIS,
    utrTypeId: utrTypeIds.MYUTR,
    isPttEvent: false,
    showAgeColumn: false,
    showRegDate: false,
    showPosition: false,
};

export default AcceptancePlayerCard;
